import React, { useEffect, useState } from 'react'
import {
  Datagrid,
  TextField,
  TextInput,
  useTranslate,
  useResourceContext,
  useResourceDefinitions,
  ListContextProvider,
  ListToolbar,
  useList,
  DateField,
  useRemoveFromStore,
  useNotify,
  useRedirect,
  Pagination,
} from 'react-admin'
import { usePaymentContext } from 'src/components/pages/payment/payment/context/paymentContext'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import { ListViewTopToolbar } from 'src/components/uiElements/CRUD/ListViewTopToolbar'
import { CardCommon } from 'src/components/uiElements'
import { setResourceProps } from 'src/helper/resourceProps'
import { Button, Box } from '@mui/material'

export const ItemPaymentReservation = () => {
  const translate = useTranslate()
  const redirect = useRedirect()
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [sort, setSort] = useState({ field: 'id', order: 'ASC' })
  const [filter, setFilter] = useState({})
  const [displayedFilters, setDisplayedFilters] = useState({})
  const [filteredData, setFilteredData] = useState([])
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  setResourceProps(useResourceDefinitions(), useResourceContext())

  const { setReservationIds, paymentData, setPaymentData } = usePaymentContext()

  const { selectedIds, onUnselectItems } = useList({ data })

  useEffect(() => {
    applyFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, data])
  
  const applyFilters = () => {
    if (!data) return
  
    let tempFilteredData = [...data]
  
    if (filter?.number) {
      const numbers = filter.number
      tempFilteredData = tempFilteredData.filter(item => numbers.includes(item.number.toString()))
    }
    setFilteredData(tempFilteredData)
  }

  useEffect(() => {
    setReservationIds(selectedIds || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds])

  useEffect(() => {
    getReservationByBusinessUnit()
    onUnselectItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getReservationByBusinessUnit = async () => {
    try {
      const businessUnitId = paymentData?.selectedBusinessUnit?.id
      setIsLoading(true)
      if (businessUnitId) {
        const reservations = await businessUnitServices.getReservationByBusinessUnitPayment({
          business_unit_id: businessUnitId,
          optional_get_user: false,
          limit: 999,
          status: 'open',
        })
        setData(reservations)
        setFilteredData(reservations)
      } else {
        setData([])
        setFilteredData([])
      }
    } catch (error) {
      setData([])
      setFilteredData([])
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const onShowFilter = (filterName) => {
    setDisplayedFilters((displayedFilters) => ({
      ...displayedFilters,
      [filterName]: true,
    }))
  }

  const onHideFilter = (filterName) => {
    const displayedFiltersAux = { ...displayedFilters }
    delete displayedFiltersAux[filterName]
    setDisplayedFilters(displayedFiltersAux)
    const filterAux = { ...filter }
    delete filterAux[filterName]
    setFilter(filterAux)
  }

  const onChangeFilters = (filters, displayedFiltersAux) => {
    if (filters?.number) {
      const numbers = filters?.number?.split(',').map(num => num.trim())
      filters.number = numbers
    }
  
    Object.entries(filters).forEach(([key, value]) => {
      if (!value) {
        delete filters[key]
      }
    })
  
    setFilter(filters)
    setDisplayedFilters(displayedFiltersAux)
  }

  const paymentFilters = [<TextInput label={translate('ra.field.number')} source='number' defaultValue='' />]

  const handlePageChange = (newPage) => {
    setPage(newPage)
  }

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage)
    setPage(1)
  }

  const paginatedData = () => {
    const paginatedData = filteredData.slice((page - 1) * perPage, page * perPage)
    if (paginatedData && paginatedData.length == 0){
      return filteredData
    }
    return paginatedData
  }
  
  const listContext = useList({
    data: paginatedData(),
    sort,
    page,
    perPage,
    filter,
    total: filteredData.length,
  })

  return (
    <>
      <CardCommon title={translate('ra.title.reservations_to_pay')}>
        <ListContextProvider
          value={{
            ...listContext,
            displayedFilters: displayedFilters,
            setFilters: onChangeFilters,
            showFilter: onShowFilter,
            hideFilter: onHideFilter,
            selectedIds: selectedIds,
          }}
        >
          <>
            <ListToolbar actions={<ListViewTopToolbar exporterDisable={true} />} filters={paymentFilters} />
            <Datagrid setSort={setSort} isLoading={isLoading} bulkActionButtons={<></>}>
              <TextField label={translate('ra.field.number')} source='number' />
              <DateField label={translate('ra.field.checkin')} source='check_in' />
              <TextField label={translate('ra.field.passenger')} source='passenger' />
            </Datagrid>
            <Pagination
                page={page}
                perPage={perPage}
                total={filteredData.length}
                setPage={handlePageChange}
                setPerPage={handlePerPageChange}
              />
          </>
        </ListContextProvider>

        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} paddingX={'20px'}>
          <Button
            className='button'
            variant='contained'
            onClick={() => {
              redirect('/payment-entity')
              setPaymentData({ ...paymentData, is_back: true })
            }}
          >
            {translate('ra.action.back')}
          </Button>

          <Button
            className='button'
            variant='contained'
            disabled={selectedIds?.length == 0}
            onClick={() => redirect('/payment-entity/payment-item')}
          >
            {translate('ra.title.see_items')}
          </Button>
        </Box>
      </CardCommon>
    </>
  )
}
